'use client';

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
// ScrollTrigger is not used here, but register if used elsewhere in the app
// import { ScrollTrigger } from 'gsap/ScrollTrigger'; 
import Image from 'next/image';
import styles from '../styles/Hero.module.css';
import gradientStyles from '../styles/GradientOverlay.module.css';
import PresaleProgress from './PresaleProgress';

// gsap.registerPlugin(ScrollTrigger); // Register plugin globally if needed

const Hero = () => {
  // Use a single main ref for context scoping
  const mainRef = useRef(null);
  // Add a state to track if PresaleProgress is loaded
  const [presaleLoaded, setPresaleLoaded] = useState(false);
  // Add ref to control animation
  const animationRef = useRef(null);

  // Handle presale loaded event
  const handlePresaleLoaded = () => {
    setPresaleLoaded(true);
  };

  useEffect(() => {
    // Only run animation once presale data is loaded
    if (!presaleLoaded) return;

    // Clear any previous animation context
    if (animationRef.current) {
      animationRef.current.revert();
    }

    // Use gsap.context for scoping and automatic cleanup
    animationRef.current = gsap.context(() => {
      // Target elements within the context
      const headerLogo = mainRef.current.querySelector(`.${styles.heroLeft} .${styles.logoImage}`);
      const intro = mainRef.current.querySelector(`.${styles.siteIntro}`);
      const presale = mainRef.current.querySelector(`.${styles.heroRight}`);

      if (!headerLogo || !intro || !presale) return;

      // Animation for the hero logo (previously header image)
      gsap.from(headerLogo, {
        y: 100,
        opacity: 0,
        duration: 1,
        delay: 0.2,
        ease: 'power3.out'
      });

      // Animation for the intro text
      gsap.from(intro, {
        y: 50,
        opacity: 0,
        duration: 1,
        delay: 0.5,
        ease: 'power3.out'
      });

      // Animation for the presale component
      gsap.from(presale, {
        x: 50, // Animate from the right
        opacity: 0,
        duration: 1,
        delay: 0.8,
        ease: 'power3.out'
      });
      
    }, mainRef); // Scope the context to the main element

    // Cleanup function
    return () => {
      if (animationRef.current) {
        animationRef.current.revert();
      }
    };
  }, [presaleLoaded]); // Run when presaleLoaded changes to true

  return (
    // Add mainRef to the root element
    <section className={styles.hero} id="hero" ref={mainRef}>
      <div className={styles.heroImg}>
        <Image 
          src="https://imagedelivery.net/LOBxX7gyPpyC22XVZKaoOw/2c3d64c7-a035-404f-67ee-8801e174e900/public" 
          alt="Background gradient" 
          className={gradientStyles.gradientImage}
          width={1920}
          height={1080}
          priority
        />
      </div>
      
      <div className={`${styles.heroContent} ${!presaleLoaded ? styles.loading : ''}`}>
        <div className={styles.heroLeft}>
          <Image 
            src="https://imagedelivery.net/LOBxX7gyPpyC22XVZKaoOw/d7c25d92-b8c1-4f1a-ed18-d91d562c2f00/public"
            alt="GameMax Logo"
            width={750}
            height={350}
            className={styles.logoImage}
            priority
          />
         
          <div id="site-intro" className={styles.siteIntro}>
            <h1 className={styles.heroHeading}>Revolutionizing Racquet Sports with AI & Web3</h1>
            <h2 className={styles.heroSubheading}>Play, earn, connect, and compete in a high-tech arena built for ballers, bashers, and paddle pros alike!</h2>
          </div>
        </div>
        
        <div className={styles.heroRight}>
          <PresaleProgress onLoaded={handlePresaleLoaded} />
        </div>
      </div>
    </section>
  );
};

export default Hero; 
